import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bot,
  Check,
  CircleAlert,
  CircleDashed,
  CircleHelp,
  Copy,
  Link2,
  Loader2,
  LogOut,
  MessageSquare,
  RefreshCw,
  Save,
  Server,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/utils/cn';
import {
  SettingsContentLoading,
  SettingsLayout,
} from '../components/settings-layout';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { HTTPStatusError } from '../lib/chat-api';
import {
  buildDiscordInstallURL,
  createDiscordDMClaimCode,
  createDiscordGuildVerificationCode,
  deleteDiscordDMLink,
  startDiscordConnect,
  type DiscordDMClaimCode,
  type DiscordDMLink,
  type DiscordGuildVerificationCode,
  listDiscordGuildConfigs,
  listDiscordGuildRoles,
  type DiscordGuildConfig,
  type DiscordGuildConfigList,
  type DiscordGuildRole,
  type DiscordInstallMetadata,
  type DiscordJoinedGuildSummary,
  upsertDiscordGuildConfig,
} from '../lib/discord';

type GuildState = 'Pending Setup' | 'Active' | 'Attention' | 'Unauthorized';

type GuildOwnershipState = 'owned_by_current_user' | 'unclaimed';

type DiscordGuildView = {
  guild_id: string;
  guild_name: string;
  allowed_role_ids: string[];
  ownership_state: GuildOwnershipState;
  install_status: string;
  shared_agent_id?: string;
  recent_error?: string;
  recent_activity_at?: string | null;
  joined_at?: string | null;
  source: 'saved' | 'joined' | 'combined';
};

type GuildDraft = {
  guildID: string;
  guildName: string;
  selectedRoleIDs: string[];
  fallbackRoleIDsText: string;
};

type GuildRolesState = {
  loading: boolean;
  error: string;
  roles: DiscordGuildRole[];
  loadedAt?: number;
};

type DMSetupState = 'Not Connected' | 'Waiting for Claim' | 'Connected';

const DEFAULT_DRAFT: GuildDraft = {
  guildID: '',
  guildName: '',
  selectedRoleIDs: [],
  fallbackRoleIDsText: '',
};

function formatDateTime(value?: string | null) {
  if (!value) {
    return 'Unknown';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Unknown';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);
}

function formatRelativeDate(value?: string | null) {
  if (!value) {
    return 'Never';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Unknown';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);
}

function formatCount(value: number) {
  return new Intl.NumberFormat('en-US').format(value);
}

function parseRoleIDs(value: string) {
  return [
    ...new Set(
      value
        .split(',')
        .map(entry => entry.trim())
        .filter(Boolean)
    ),
  ].sort();
}

function normalizeGuildName(view: DiscordGuildView) {
  const name = view.guild_name.trim();
  if (name && name !== view.guild_id) {
    return name;
  }
  return 'Discord server';
}

function guildSubtitle(view: DiscordGuildView) {
  const name = view.guild_name.trim();
  if (name && name !== view.guild_id) {
    return 'Connected Discord server';
  }
  if (view.source === 'joined') {
    return 'Name will appear after Bridge refreshes Discord';
  }
  return 'Server name not refreshed yet';
}

function normalizeGuildState(view: DiscordGuildView): GuildState {
  const installStatus = view.install_status.trim().toLowerCase();
  const hasRoles = view.allowed_role_ids.length > 0;
  const hasAgent = Boolean(view.shared_agent_id?.trim());

  if (installStatus === 'left') {
    return 'Unauthorized';
  }

  if (installStatus === 'error' || view.recent_error?.trim()) {
    return 'Attention';
  }

  if (installStatus === 'joined' && hasRoles && hasAgent) {
    return 'Active';
  }

  if (
    installStatus === 'authorized' &&
    hasRoles &&
    hasAgent &&
    view.joined_at
  ) {
    return 'Active';
  }

  return 'Pending Setup';
}

function guildStateCopy(state: GuildState) {
  switch (state) {
    case 'Active':
      return {
        tone: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
        description: 'Listening for mentions and ready to respond.',
        cta: 'Edit Roles',
      } as const;
    case 'Attention':
      return {
        tone: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
        description:
          'Something needs your attention before messages can be routed.',
        cta: 'Review',
      } as const;
    case 'Unauthorized':
      return {
        tone: 'bg-rose-500/10 text-rose-700 dark:text-rose-400',
        description: 'The bot is no longer authorized for this server.',
        cta: 'Review',
      } as const;
    case 'Pending Setup':
    default:
      return {
        tone: 'bg-sky-500/10 text-sky-700 dark:text-sky-400',
        description:
          'Finish connecting and choose which roles can use the bot.',
        cta: 'Finish Setup',
      } as const;
  }
}

function buildGuildViews(
  configs: DiscordGuildConfig[],
  joinedGuilds: DiscordJoinedGuildSummary[]
) {
  const configByGuildID = new Map<string, DiscordGuildConfig>();
  for (const config of configs) {
    configByGuildID.set(config.guild_id, config);
  }

  const joinedByGuildID = new Map<string, DiscordJoinedGuildSummary>();
  for (const joined of joinedGuilds) {
    if (joined.ownership_state?.trim() === 'owned_by_other_user') {
      continue;
    }
    joinedByGuildID.set(joined.guild_id, joined);
  }

  const guildIDs = new Set<string>([
    ...configByGuildID.keys(),
    ...joinedByGuildID.keys(),
  ]);

  const views: DiscordGuildView[] = [];
  for (const guildID of guildIDs) {
    const saved = configByGuildID.get(guildID);
    const joined = joinedByGuildID.get(guildID);
    const name =
      saved?.guild_name?.trim() || joined?.guild_name?.trim() || guildID;
    const savedInstallStatus = saved?.install_status?.trim() || '';
    const joinedInstallStatus = joined?.install_status?.trim() || '';
    let installStatus = savedInstallStatus;

    if (joined) {
      installStatus = joinedInstallStatus || 'joined';
    } else if (savedInstallStatus.toLowerCase() === 'joined') {
      installStatus = 'left';
    } else if (!installStatus) {
      installStatus = 'authorized';
    }

    views.push({
      guild_id: guildID,
      guild_name: name,
      allowed_role_ids: saved?.allowed_role_ids ?? [],
      ownership_state:
        (saved?.ownership_state?.trim() as GuildOwnershipState | undefined) ||
        (joined?.ownership_state?.trim() as GuildOwnershipState | undefined) ||
        (saved ? 'owned_by_current_user' : 'unclaimed'),
      install_status: installStatus,
      shared_agent_id: saved?.shared_agent_id || joined?.shared_agent_id || '',
      recent_error: saved?.recent_error || joined?.recent_error || '',
      recent_activity_at:
        saved?.recent_activity_at ?? joined?.recent_activity_at ?? null,
      joined_at: saved?.joined_at ?? joined?.joined_at ?? null,
      source: saved && joined ? 'combined' : saved ? 'saved' : 'joined',
    });
  }

  return views.sort((a, b) => {
    const statePriority: Record<GuildState, number> = {
      'Pending Setup': 0,
      Attention: 1,
      Active: 2,
      Unauthorized: 3,
    };
    const aState = normalizeGuildState(a);
    const bState = normalizeGuildState(b);
    const stateDelta = statePriority[aState] - statePriority[bState];
    if (stateDelta !== 0) {
      return stateDelta;
    }

    const aTimestamp = a.recent_activity_at
      ? new Date(a.recent_activity_at).getTime()
      : 0;
    const bTimestamp = b.recent_activity_at
      ? new Date(b.recent_activity_at).getTime()
      : 0;
    if (aTimestamp !== bTimestamp) {
      return bTimestamp - aTimestamp;
    }

    return a.guild_name.localeCompare(b.guild_name);
  });
}

function formatRoleSummary(
  selectedIDs: string[],
  roleLookup: Map<string, DiscordGuildRole>
) {
  if (selectedIDs.length === 0) {
    return 'No roles selected';
  }

  const names = selectedIDs
    .map(roleID => roleLookup.get(roleID)?.name?.trim() || '')
    .filter(Boolean);

  if (names.length === selectedIDs.length) {
    return names.join(', ');
  }

  if (names.length > 0) {
    return `${names.join(', ')} + ${selectedIDs.length - names.length} more`;
  }

  return `${formatCount(selectedIDs.length)} roles selected`;
}

function getInstallButtonLabel(metadata?: DiscordInstallMetadata | null) {
  if (buildDiscordInstallURL(metadata)) {
    return 'Add Discord Server';
  }
  return 'Setup needed';
}

function getDiscordBotDisplayName(metadata?: DiscordInstallMetadata | null) {
  const botName = metadata?.bot_name?.trim() || metadata?.bot_username?.trim();
  if (botName) {
    return botName;
  }
  const appName = metadata?.application_name?.trim();
  if (appName) {
    return appName;
  }
  return 'the Discord bot';
}

function currentPathWithSearch() {
  return `${window.location.pathname}${window.location.search}`;
}

function getDMSetupState(
  dmLink?: DiscordDMLink | null,
  dmClaimCode?: DiscordDMClaimCode | null
): DMSetupState {
  if (dmLink?.discord_user_id?.trim()) {
    return 'Connected';
  }
  if (dmClaimCode?.code?.trim()) {
    return 'Waiting for Claim';
  }
  return 'Not Connected';
}

function dmSetupStateTone(state: DMSetupState) {
  switch (state) {
    case 'Connected':
      return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400';
    case 'Waiting for Claim':
      return 'bg-sky-500/10 text-sky-700 dark:text-sky-400';
    case 'Not Connected':
    default:
      return 'bg-muted text-muted-foreground';
  }
}

function humanInstallStatus(status: string) {
  switch (status.trim().toLowerCase()) {
    case 'joined':
      return 'Bot is in the server';
    case 'authorized':
      return 'Authorized, waiting to join';
    case 'left':
      return 'Bot was removed';
    case 'error':
      return 'Connection error';
    default:
      return status || 'Unknown';
  }
}

function HelpTip({ label, children }: { label: string; children: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="inline-flex size-4 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/20"
          aria-label={`More info about ${label}`}
        >
          <CircleHelp className="size-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" align="center">
        {children}
      </TooltipContent>
    </Tooltip>
  );
}

function LabelWithHelp({
  label,
  tooltip,
  className,
}: {
  label: string;
  tooltip: string;
  className?: string;
}) {
  return (
    <div className={cn('inline-flex items-center gap-1.5', className)}>
      <span>{label}</span>
      <HelpTip label={label}>{tooltip}</HelpTip>
    </div>
  );
}

function StatusPill({
  label,
  className,
}: {
  label: string;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium',
        className
      )}
    >
      <span className="size-1.5 rounded-full bg-current" />
      {label}
    </span>
  );
}

export function DiscordGuildSettingsPage() {
  const navigate = useNavigate();
  const [pageData, setPageData] = useState<DiscordGuildConfigList | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [selectedGuildID, setSelectedGuildID] = useState<string | null>(null);
  const [draft, setDraft] = useState<GuildDraft>(DEFAULT_DRAFT);
  const [rolesByGuildID, setRolesByGuildID] = useState<
    Record<string, GuildRolesState>
  >({});
  const [dmAction, setDMAction] = useState<
    'claim' | 'oauth' | 'disconnect' | null
  >(null);
  const [copyingClaimCode, setCopyingClaimCode] = useState(false);
  const [guildVerificationCode, setGuildVerificationCode] =
    useState<DiscordGuildVerificationCode | null>(null);
  const [guildVerificationLoading, setGuildVerificationLoading] =
    useState(false);
  const [copyingGuildVerificationCode, setCopyingGuildVerificationCode] =
    useState(false);
  const [guildVerificationPrimed, setGuildVerificationPrimed] = useState(false);

  const isUnauthorizedError = useCallback((value: unknown) => {
    if (value instanceof HTTPStatusError && value.status === 401) {
      return true;
    }
    if (
      typeof value === 'object' &&
      value !== null &&
      'status' in value &&
      (value as { status?: unknown }).status === 401
    ) {
      return true;
    }
    return false;
  }, []);

  const redirectToLogin = useCallback(() => {
    navigate(`/login?next=${encodeURIComponent(currentPathWithSearch())}`, {
      replace: true,
    });
  }, [navigate]);

  const loadPage = useCallback(
    async (options?: { silent?: boolean }) => {
      const silent = options?.silent ?? false;
      if (!silent) {
        setLoading(true);
      }
      setError('');

      try {
        const nextGuilds = await listDiscordGuildConfigs();
        setPageData(nextGuilds);
        setGuildVerificationCode(null);
        setGuildVerificationPrimed(false);
      } catch (loadError) {
        if (isUnauthorizedError(loadError)) {
          redirectToLogin();
          return;
        }
        setError(
          loadError instanceof Error
            ? loadError.message
            : 'Failed to load Discord integrations'
        );
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [isUnauthorizedError, redirectToLogin]
  );

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  const guildViews = useMemo(
    () =>
      buildGuildViews(pageData?.guilds ?? [], pageData?.joined_guilds ?? []),
    [pageData?.guilds, pageData?.joined_guilds]
  );

  const selectedGuild = useMemo(
    () =>
      selectedGuildID
        ? (guildViews.find(view => view.guild_id === selectedGuildID) ?? null)
        : null,
    [guildViews, selectedGuildID]
  );

  const selectedGuildNeedsVerification =
    selectedGuild?.ownership_state === 'unclaimed';
  const hasGuildVerificationCode = Boolean(guildVerificationCode?.code?.trim());
  const showGuildVerificationWorkflow =
    guildVerificationPrimed ||
    selectedGuildNeedsVerification ||
    hasGuildVerificationCode ||
    guildViews.length === 0;

  useEffect(() => {
    if (guildViews.length === 0) {
      if (selectedGuildID !== null) {
        setSelectedGuildID(null);
      }
      return;
    }

    const stillExists =
      selectedGuildID !== null &&
      guildViews.some(view => view.guild_id === selectedGuildID);
    if (stillExists) {
      return;
    }

    const preferred =
      guildViews.find(view => normalizeGuildState(view) === 'Pending Setup') ??
      guildViews[0];
    setSelectedGuildID(preferred.guild_id);
  }, [guildViews, selectedGuildID]);

  useEffect(() => {
    if (!selectedGuild) {
      return;
    }

    setDraft({
      guildID: selectedGuild.guild_id,
      guildName: selectedGuild.guild_name,
      selectedRoleIDs: selectedGuild.allowed_role_ids,
      fallbackRoleIDsText: selectedGuild.allowed_role_ids.join(', '),
    });
  }, [selectedGuild]);

  useEffect(() => {
    if (selectedGuildNeedsVerification || hasGuildVerificationCode) {
      setGuildVerificationPrimed(true);
    }
  }, [hasGuildVerificationCode, selectedGuildNeedsVerification]);

  const selectedGuildRolesState =
    (selectedGuild ? rolesByGuildID[selectedGuild.guild_id] : undefined) ??
    ({
      loading: false,
      error: '',
      roles: [],
    } satisfies GuildRolesState);

  const selectedGuildRoleLookup = useMemo(() => {
    return new Map(
      selectedGuildRolesState.roles.map(role => [role.id, role] as const)
    );
  }, [selectedGuildRolesState.roles]);

  const installURL = useMemo(
    () => buildDiscordInstallURL(pageData?.install_metadata ?? null),
    [pageData?.install_metadata]
  );
  const dmLink = pageData?.dm_link ?? null;
  const dmClaimCode = pageData?.dm_claim_code ?? null;
  const discordOAuthEnabled = Boolean(pageData?.official_login_enabled);
  const dmSetupState = useMemo(
    () => getDMSetupState(dmLink, dmClaimCode),
    [dmClaimCode, dmLink]
  );
  const discordBotDisplayName = useMemo(
    () => getDiscordBotDisplayName(pageData?.install_metadata ?? null),
    [pageData?.install_metadata]
  );
  const dmSetupReady = Boolean(
    pageData?.runtime?.configured &&
    (pageData?.install_metadata?.bot_user_id?.trim() ||
      pageData?.install_metadata?.bot_username?.trim() ||
      pageData?.install_metadata?.application_id?.trim())
  );
  const loadGuildRoles = useCallback(
    async (guildID: string, options?: { force?: boolean }) => {
      const force = options?.force ?? false;
      let shouldLoad = true;

      setRolesByGuildID(current => {
        const currentState = current[guildID];
        if (currentState?.loading) {
          shouldLoad = false;
          return current;
        }
        if (currentState && !force && currentState.roles.length > 0) {
          shouldLoad = false;
          return current;
        }

        shouldLoad = true;
        return {
          ...current,
          [guildID]: {
            loading: true,
            error: '',
            roles: currentState?.roles ?? [],
            loadedAt: currentState?.loadedAt,
          },
        };
      });

      if (!shouldLoad) {
        return;
      }

      try {
        const result = await listDiscordGuildRoles(guildID);
        setRolesByGuildID(current => ({
          ...current,
          [guildID]: {
            loading: false,
            error: '',
            roles: result.roles,
            loadedAt: Date.now(),
          },
        }));
      } catch (loadRolesError) {
        setRolesByGuildID(current => ({
          ...current,
          [guildID]: {
            loading: false,
            error:
              loadRolesError instanceof Error
                ? loadRolesError.message
                : 'Failed to load Discord roles',
            roles: current[guildID]?.roles ?? [],
            loadedAt: current[guildID]?.loadedAt,
          },
        }));
      }
    },
    []
  );

  useEffect(() => {
    if (!selectedGuild) {
      return;
    }
    void loadGuildRoles(selectedGuild.guild_id);
  }, [loadGuildRoles, selectedGuild]);

  const handleAddDiscordServer = useCallback(() => {
    if (!installURL) {
      setNotice('');
      setError(
        'The Discord install link is not ready yet. Refresh once Bridge finishes setup.'
      );
      return;
    }

    setGuildVerificationPrimed(true);
    setError('');
    setNotice(
      'Finish adding the bot in Discord, then generate a verification code here and run /bridgebot link in the server you want to connect.'
    );
    window.open(installURL, '_blank', 'noopener,noreferrer');
  }, [installURL]);

  const handleOpenDiscordDM = useCallback(() => {
    window.open(
      'https://discord.com/channels/@me',
      '_blank',
      'noopener,noreferrer'
    );
  }, []);

  const handleCreateDMClaim = useCallback(async () => {
    if (!dmSetupReady) {
      setNotice('');
      setError(
        'Discord direct messages will unlock after Discord setup is finished.'
      );
      return;
    }

    setDMAction('claim');
    setError('');
    setNotice('');

    try {
      const nextClaim = await createDiscordDMClaimCode();
      setPageData(current =>
        current
          ? {
              ...current,
              dm_claim_code: nextClaim,
            }
          : current
      );
      setNotice(
        'Discord fallback code ready. In the Discord DM, run /bridgebot link and paste this code.'
      );
    } catch (claimError) {
      if (isUnauthorizedError(claimError)) {
        redirectToLogin();
        return;
      }
      setError(
        claimError instanceof Error
          ? claimError.message
          : 'Failed to create Discord fallback code'
      );
    } finally {
      setDMAction(null);
    }
  }, [dmSetupReady, isUnauthorizedError, redirectToLogin]);

  const handleConnectDiscord = useCallback(async () => {
    if (!dmSetupReady) {
      setNotice('');
      setError(
        'Discord direct messages will unlock after Discord setup is finished.'
      );
      return;
    }
    if (!discordOAuthEnabled) {
      await handleCreateDMClaim();
      return;
    }

    const width = 540;
    const height = 720;
    const left = Math.max(window.screenX + (window.outerWidth - width) / 2, 0);
    const top = Math.max(window.screenY + (window.outerHeight - height) / 2, 0);
    const popup = window.open(
      'about:blank',
      'cueboard-discord-connect',
      `width=${width},height=${height},left=${left},top=${top},popup=yes`
    );
    if (!popup) {
      setError('Please allow popups to connect Discord.');
      return;
    }

    setDMAction('oauth');
    setError('');
    setNotice('');

    try {
      const result = await startDiscordConnect();
      popup.location.href = result.authorization_url;
      popup.focus();
      setNotice(
        'Discord opened. This page will update after you confirm the connection.'
      );
    } catch (connectError) {
      popup.close();
      if (isUnauthorizedError(connectError)) {
        redirectToLogin();
        return;
      }
      setError(
        connectError instanceof Error
          ? connectError.message
          : 'Failed to start Discord connection'
      );
    } finally {
      setDMAction(null);
    }
  }, [
    discordOAuthEnabled,
    dmSetupReady,
    handleCreateDMClaim,
    isUnauthorizedError,
    redirectToLogin,
  ]);

  const handleCopyDMClaimCode = useCallback(async () => {
    const code = dmClaimCode?.code?.trim();
    if (!code) {
      return;
    }

    setCopyingClaimCode(true);
    setError('');
    setNotice('');
    try {
      await navigator.clipboard.writeText(code);
      setNotice(
        'Discord fallback code copied. In the Discord DM, run /bridgebot link and paste this code.'
      );
    } catch (copyError) {
      setError(
        copyError instanceof Error
          ? copyError.message
          : 'Failed to copy the Discord fallback code'
      );
    } finally {
      setCopyingClaimCode(false);
    }
  }, [dmClaimCode?.code]);

  const handleCreateGuildVerificationCode = useCallback(async () => {
    setGuildVerificationPrimed(true);
    setGuildVerificationLoading(true);
    setError('');
    setNotice('');

    try {
      const nextCode = await createDiscordGuildVerificationCode();
      setGuildVerificationCode(nextCode);
      setNotice(
        'Verification code ready. In the Discord server you want to connect, run /bridgebot link and paste this code, then refresh this page.'
      );
    } catch (verificationError) {
      if (isUnauthorizedError(verificationError)) {
        redirectToLogin();
        return;
      }
      setError(
        verificationError instanceof Error
          ? verificationError.message
          : 'Failed to create Discord verification code'
      );
    } finally {
      setGuildVerificationLoading(false);
    }
  }, [isUnauthorizedError, redirectToLogin]);

  const handleCopyGuildVerificationCode = useCallback(async () => {
    const code = guildVerificationCode?.code?.trim();
    if (!code) {
      return;
    }
    setCopyingGuildVerificationCode(true);
    setError('');
    setNotice('');
    try {
      await navigator.clipboard.writeText(code);
      setNotice(
        'Verification code copied. Run /bridgebot link in the Discord server you want to connect, then paste this code.'
      );
    } catch (copyError) {
      setError(
        copyError instanceof Error
          ? copyError.message
          : 'Failed to copy the verification code'
      );
    } finally {
      setCopyingGuildVerificationCode(false);
    }
  }, [guildVerificationCode?.code]);

  const handleDisconnectDM = useCallback(async () => {
    setDMAction('disconnect');
    setError('');
    setNotice('');

    try {
      await deleteDiscordDMLink();
      await loadPage({ silent: true });
      setNotice('Discord DM disconnected.');
    } catch (disconnectError) {
      if (isUnauthorizedError(disconnectError)) {
        redirectToLogin();
        return;
      }
      setError(
        disconnectError instanceof Error
          ? disconnectError.message
          : 'Failed to disconnect Discord DM'
      );
    } finally {
      setDMAction(null);
    }
  }, [isUnauthorizedError, loadPage, redirectToLogin]);

  const handleSelectGuild = useCallback((guildID: string) => {
    setSelectedGuildID(guildID);
    setNotice('');
    setError('');
  }, []);

  useEffect(() => {
    const code = dmClaimCode?.code?.trim();
    if (!code) {
      return;
    }

    const timer = window.setInterval(() => {
      void loadPage({ silent: true });
    }, 4000);

    return () => {
      window.clearInterval(timer);
    };
  }, [dmClaimCode?.code, loadPage]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }
      const data = event.data as {
        type?: unknown;
        ok?: unknown;
        message?: unknown;
      };
      if (data?.type !== 'cueboard-discord-connect-result') {
        return;
      }
      const message =
        typeof data.message === 'string' && data.message.trim()
          ? data.message
          : data.ok
            ? 'Discord connected.'
            : 'Discord connection failed.';
      if (data.ok) {
        setError('');
        setNotice(message);
        void loadPage({ silent: true });
      } else {
        setNotice('');
        setError(message);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [loadPage]);

  const handleRoleToggle = useCallback((roleID: string) => {
    setDraft(current => {
      const nextIDs = current.selectedRoleIDs.includes(roleID)
        ? current.selectedRoleIDs.filter(currentID => currentID !== roleID)
        : [...current.selectedRoleIDs, roleID].sort();

      return {
        ...current,
        selectedRoleIDs: nextIDs,
        fallbackRoleIDsText: nextIDs.join(', '),
      };
    });
  }, []);

  const handleSave = useCallback(async () => {
    const guildID = draft.guildID.trim();
    if (!guildID) {
      setError('Select a Discord server first.');
      return;
    }

    const currentGuild = guildViews.find(view => view.guild_id === guildID);
    if (currentGuild?.ownership_state === 'unclaimed') {
      setError(
        'Link this Discord server in Discord with /bridgebot link before saving roles.'
      );
      return;
    }
    const roleIDs =
      selectedGuildRolesState.roles.length > 0
        ? draft.selectedRoleIDs
        : parseRoleIDs(draft.fallbackRoleIDsText);

    setSaving(true);
    setError('');
    setNotice('');

    try {
      await upsertDiscordGuildConfig(guildID, {
        guildName:
          draft.guildName.trim() || currentGuild?.guild_name?.trim() || guildID,
        allowedRoleIDs: roleIDs,
      });
      setNotice(
        currentGuild
          ? `${normalizeGuildName(currentGuild)} updated.`
          : 'Discord server added.'
      );
      await loadPage({ silent: true });
      setSelectedGuildID(guildID);
    } catch (saveError) {
      if (isUnauthorizedError(saveError)) {
        redirectToLogin();
        return;
      }
      setError(
        saveError instanceof Error
          ? saveError.message
          : 'Failed to save Discord server'
      );
    } finally {
      setSaving(false);
    }
  }, [
    draft.fallbackRoleIDsText,
    draft.guildID,
    draft.guildName,
    draft.selectedRoleIDs,
    guildViews,
    isUnauthorizedError,
    loadPage,
    redirectToLogin,
    selectedGuildRolesState.roles.length,
  ]);

  return (
    <SettingsLayout
      current="discord"
      notice={notice}
      error={error}
      onNoticeDismiss={() => setNotice('')}
      onErrorDismiss={() => setError('')}
    >
      {loading ? (
        <SettingsContentLoading label="Loading Discord settings..." />
      ) : (
        <>
          <Card>
            <CardHeader className="border-b px-6 py-4">
              <CardTitle className="flex items-center gap-2 text-base font-medium">
                <MessageSquare className="size-4 text-muted-foreground" />
                Discord direct messages
              </CardTitle>
              <CardDescription>
                Connect one Discord account to Bridge, then keep using normal
                DMs without repeating setup.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 p-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(280px,360px)]">
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <StatusPill
                    label={dmSetupState}
                    className={dmSetupStateTone(dmSetupState)}
                  />
                  <span className="text-xs text-muted-foreground">
                    Bot: {discordBotDisplayName}
                  </span>
                </div>

                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="rounded-lg border bg-muted/20 p-4">
                    <div className="text-xs font-medium text-muted-foreground">
                      Step 1
                    </div>
                    <div className="mt-1 text-sm font-medium text-foreground">
                      Connect from Bridge
                    </div>
                    <p className="mt-1 text-xs leading-5 text-muted-foreground">
                      Confirm the connection in Discord. Use a short-lived code
                      only as a fallback.
                    </p>
                  </div>
                  <div className="rounded-lg border bg-muted/20 p-4">
                    <div className="text-xs font-medium text-muted-foreground">
                      Step 2
                    </div>
                    <div className="mt-1 text-sm font-medium text-foreground">
                      Open the Discord DM
                    </div>
                    <p className="mt-1 text-xs leading-5 text-muted-foreground">
                      Message {discordBotDisplayName}. If you are using a
                      fallback code, run{' '}
                      <span className="font-mono">/bridgebot link</span> and
                      paste the code there.
                    </p>
                  </div>
                  <div className="rounded-lg border bg-muted/20 p-4">
                    <div className="text-xs font-medium text-muted-foreground">
                      Step 3
                    </div>
                    <div className="mt-1 text-sm font-medium text-foreground">
                      Wait for Connected
                    </div>
                    <p className="mt-1 text-xs leading-5 text-muted-foreground">
                      This page updates automatically. Once connected, normal
                      DMs go straight to Bridge.
                    </p>
                  </div>
                </div>

                {dmClaimCode?.code?.trim() ? (
                  <div className="rounded-lg border bg-muted/20 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="space-y-1">
                        <div className="text-xs font-medium text-muted-foreground">
                          Fallback code
                        </div>
                        <div className="font-mono text-2xl font-semibold tracking-[0.2em] text-foreground">
                          {dmClaimCode.code}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Expires {formatDateTime(dmClaimCode.expires_at)}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => void handleCopyDMClaimCode()}
                          disabled={copyingClaimCode}
                        >
                          {copyingClaimCode ? (
                            <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                          ) : (
                            <Copy className="mr-1.5 size-3.5" />
                          )}
                          Copy code
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => void handleOpenDiscordDM()}
                        >
                          <Link2 className="mr-1.5 size-3.5" />
                          Open Discord
                        </Button>
                      </div>
                    </div>
                    {dmLink?.discord_user_id?.trim() ? (
                      <p className="mt-3 text-xs leading-5 text-muted-foreground">
                        A new fallback code is pending. Your current Discord DM
                        stays connected until this code is used from another
                        account.
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div className="space-y-4 rounded-lg border bg-muted/10 p-4">
                <div className="space-y-1">
                  <div className="text-xs font-medium text-muted-foreground">
                    Current DM connection
                  </div>
                  <div className="text-sm font-medium text-foreground">
                    {dmLink?.discord_user_id?.trim()
                      ? 'Connected'
                      : 'No Discord account connected yet'}
                  </div>
                  <p className="text-xs leading-5 text-muted-foreground">
                    {dmLink?.discord_user_id?.trim()
                      ? 'If this is the wrong Discord account, disconnect it or generate a fallback code to move DM access.'
                      : 'Server setup is separate. Connecting DMs lets your own Discord account talk to Bridge in private.'}
                  </p>
                  {!dmSetupReady ? (
                    <p className="text-xs leading-5 text-amber-700 dark:text-amber-400">
                      Direct messages will unlock after Discord setup is
                      finished.
                    </p>
                  ) : null}
                </div>

                {dmLink?.discord_user_id?.trim() ? (
                  <div className="space-y-3 rounded-lg border bg-background p-3">
                    <div className="space-y-1">
                      <div className="text-xs font-medium text-muted-foreground">
                        Discord username
                      </div>
                      <div className="break-all text-xs text-foreground">
                        {dmLink.discord_username?.trim() || 'Unknown'}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-xs font-medium text-muted-foreground">
                        Linked at
                      </div>
                      <div className="text-xs text-foreground">
                        {formatDateTime(dmLink.created_at)}
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="flex flex-col gap-2">
                  <Button
                    size="sm"
                    onClick={() => void handleConnectDiscord()}
                    disabled={
                      dmAction === 'claim' ||
                      dmAction === 'oauth' ||
                      !dmSetupReady
                    }
                  >
                    {dmAction === 'claim' || dmAction === 'oauth' ? (
                      <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                    ) : discordOAuthEnabled ? (
                      <Link2 className="mr-1.5 size-3.5" />
                    ) : (
                      <MessageSquare className="mr-1.5 size-3.5" />
                    )}
                    {discordOAuthEnabled
                      ? dmLink?.discord_user_id?.trim()
                        ? 'Reconnect with Discord'
                        : 'Connect with Discord'
                      : dmLink?.discord_user_id?.trim()
                        ? 'Generate reconnect code'
                        : 'Generate fallback code'}
                  </Button>
                  {discordOAuthEnabled ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => void handleCreateDMClaim()}
                      disabled={dmAction === 'claim' || !dmSetupReady}
                    >
                      {dmAction === 'claim' ? (
                        <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                      ) : (
                        <MessageSquare className="mr-1.5 size-3.5" />
                      )}
                      Generate fallback code
                    </Button>
                  ) : null}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void handleDisconnectDM()}
                    disabled={
                      dmAction === 'disconnect' ||
                      !dmLink?.discord_user_id?.trim()
                    }
                  >
                    {dmAction === 'disconnect' ? (
                      <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                    ) : (
                      <LogOut className="mr-1.5 size-3.5" />
                    )}
                    Disconnect DM
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-4 xl:grid-cols-[minmax(0,1.1fr)_minmax(360px,420px)]">
            <Card className="overflow-hidden">
              <CardHeader className="border-b px-6 py-4">
                <CardTitle className="flex items-center gap-2 text-base font-medium">
                  <Server className="size-4 text-muted-foreground" />
                  Discord servers
                </CardTitle>
                <CardDescription>
                  Choose a server to finish setup, review access, or adjust the
                  allowed roles.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {guildViews.length === 0 ? (
                  <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
                    <div className="flex size-10 items-center justify-center rounded-lg border bg-muted/30">
                      <Server className="size-5 text-muted-foreground" />
                    </div>
                    <div className="space-y-1">
                      <h3 className="text-sm font-medium text-foreground">
                        No Discord servers yet
                      </h3>
                      <p className="max-w-sm text-sm leading-6 text-muted-foreground">
                        Install the bot into a server, then come back here to
                        choose the roles that can mention it.
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={handleAddDiscordServer}
                      disabled={!installURL}
                    >
                      <Server className="mr-1.5 size-3.5" />
                      {getInstallButtonLabel(
                        pageData?.install_metadata ?? null
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3 p-3">
                    {guildViews.map(guild => {
                      const state = normalizeGuildState(guild);
                      const stateCopy = guildStateCopy(state);
                      const isSelected = selectedGuildID === guild.guild_id;

                      return (
                        <button
                          key={guild.guild_id}
                          type="button"
                          aria-pressed={isSelected}
                          className={cn(
                            'w-full rounded-lg border px-4 py-4 text-left transition-[border-color,background-color,box-shadow] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/20',
                            isSelected
                              ? 'border-primary/40 bg-primary/[0.05] shadow-sm ring-1 ring-primary/15'
                              : 'border-border bg-background hover:border-foreground/10 hover:bg-muted/20'
                          )}
                          onClick={() => handleSelectGuild(guild.guild_id)}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0 space-y-1">
                              <div className="flex items-center gap-2">
                                <span className="truncate text-sm font-medium text-foreground">
                                  {normalizeGuildName(guild)}
                                </span>
                                {isSelected ? (
                                  <span className="rounded-md bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary">
                                    Selected
                                  </span>
                                ) : null}
                                {guild.source === 'joined' ? (
                                  <span className="rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                                    New
                                  </span>
                                ) : null}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {stateCopy.description}
                              </div>
                            </div>
                            <StatusPill
                              label={state}
                              className={stateCopy.tone}
                            />
                          </div>

                          <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                            <div className="flex items-center gap-1.5">
                              <Users className="size-3.5" />
                              <span>
                                {`${guild.allowed_role_ids.length} ${
                                  guild.allowed_role_ids.length === 1
                                    ? 'role'
                                    : 'roles'
                                }`}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Bot className="size-3.5" />
                              <span
                                className={
                                  guild.shared_agent_id?.trim()
                                    ? 'text-emerald-600 dark:text-emerald-400'
                                    : 'text-amber-600 dark:text-amber-400'
                                }
                              >
                                {guild.shared_agent_id?.trim()
                                  ? 'Bridge ready'
                                  : 'Bridge pending'}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <RefreshCw className="size-3.5" />
                              <span>
                                {formatRelativeDate(guild.recent_activity_at)}
                              </span>
                            </div>
                          </div>

                          {guild.recent_error?.trim() ? (
                            <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
                              <CircleAlert className="mt-0.5 size-4 shrink-0" />
                              <span>{guild.recent_error}</span>
                            </div>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex flex-col gap-4 xl:sticky xl:top-6 xl:self-start">
              <Card>
                <CardHeader className="border-b px-6 py-4">
                  <CardTitle className="flex items-center gap-2 text-base font-medium">
                    <Bot className="size-4 text-muted-foreground" />
                    Setup panel
                  </CardTitle>
                  <CardDescription>
                    Confirm the selected server, sync its roles, and save the
                    access policy.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6 p-6">
                  {selectedGuild ? (
                    <>
                      <div className="space-y-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="space-y-1">
                            <h3 className="text-lg font-medium text-foreground">
                              {normalizeGuildName(selectedGuild)}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {guildSubtitle(selectedGuild)}
                            </p>
                          </div>
                          <StatusPill
                            label={normalizeGuildState(selectedGuild)}
                            className={
                              guildStateCopy(normalizeGuildState(selectedGuild))
                                .tone
                            }
                          />
                        </div>

                        <div className="grid gap-3 sm:grid-cols-2">
                          <div className="rounded-lg border bg-muted/20 p-4">
                            <div className="text-xs font-medium text-muted-foreground">
                              Connection
                            </div>
                            <div className="mt-1 text-sm font-medium text-foreground">
                              {humanInstallStatus(selectedGuild.install_status)}
                            </div>
                          </div>
                          <div className="rounded-lg border bg-muted/20 p-4">
                            <LabelWithHelp
                              label="Bridge status"
                              className="text-xs font-medium text-muted-foreground"
                              tooltip="Discord server admin rights decide whether the bot can be added or removed in Discord. Bridge keeps the saved role policy here."
                            />
                            <div
                              className={cn(
                                'mt-1 text-sm font-medium',
                                selectedGuild.shared_agent_id?.trim()
                                  ? 'text-emerald-600 dark:text-emerald-400'
                                  : 'text-muted-foreground'
                              )}
                            >
                              {selectedGuild.shared_agent_id?.trim()
                                ? 'Ready in Bridge'
                                : 'Not provisioned'}
                            </div>
                          </div>
                        </div>

                        {selectedGuild.ownership_state === 'unclaimed' ? (
                          <div className="space-y-3 rounded-lg border border-sky-500/20 bg-sky-500/10 p-4 text-sm text-sky-900 dark:text-sky-100">
                            <div className="font-medium">
                              This Discord server still needs Bridge setup.
                            </div>
                            <div className="text-sky-800/90 dark:text-sky-100/80">
                              Use the setup card on the right to generate one
                              code, then run{' '}
                              <span className="font-mono">/bridgebot link</span>{' '}
                              inside this Discord server from a Discord account
                              that can manage the server.
                            </div>
                          </div>
                        ) : null}

                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-foreground">
                              Display name
                            </label>
                            <HelpTip label="display name">
                              This name is shown inside Bridge so people can
                              recognize the server here.
                            </HelpTip>
                          </div>
                          <Input
                            value={draft.guildName}
                            disabled={saving}
                            onChange={event =>
                              setDraft(current => ({
                                ...current,
                                guildName: event.target.value,
                              }))
                            }
                            placeholder="Bridge Engineering"
                          />
                          <p className="text-xs text-muted-foreground">
                            Used only inside Bridge. Discord keeps its own
                            server name.
                          </p>
                        </div>
                      </div>

                      <div className="space-y-4 border-t pt-5">
                        <div className="flex items-start justify-between gap-4">
                          <div className="space-y-1">
                            <LabelWithHelp
                              label="Allowed roles"
                              className="text-sm font-medium text-foreground"
                              tooltip="Only members with one of these Discord roles can mention the bot."
                            />
                            <div className="text-xs text-muted-foreground">
                              Choose the Discord roles allowed to talk to the
                              bot.
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              void loadGuildRoles(selectedGuild.guild_id, {
                                force: true,
                              })
                            }
                            disabled={
                              selectedGuildRolesState.loading ||
                              selectedGuild.ownership_state === 'unclaimed'
                            }
                          >
                            {selectedGuildRolesState.loading ? (
                              <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                            ) : (
                              <RefreshCw className="mr-1.5 size-3.5" />
                            )}
                            Refresh roles
                          </Button>
                        </div>

                        {selectedGuildRolesState.error ? (
                          <div className="rounded-md border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
                            {selectedGuildRolesState.error}
                          </div>
                        ) : null}

                        {selectedGuild.ownership_state === 'unclaimed' ? (
                          <div className="rounded-lg border bg-muted/20 px-4 py-8 text-center">
                            <ShieldCheck className="mx-auto mb-2 size-5 text-muted-foreground" />
                            <div className="text-sm font-medium text-foreground">
                              Verify this server before choosing roles
                            </div>
                            <p className="mt-1 text-sm text-muted-foreground">
                              Role access becomes editable after{' '}
                              <span className="font-mono">/bridgebot link</span>{' '}
                              succeeds in Discord.
                            </p>
                          </div>
                        ) : selectedGuildRolesState.loading &&
                          selectedGuildRolesState.roles.length === 0 ? (
                          <div className="rounded-lg border bg-muted/20 px-4 py-8 text-center">
                            <Loader2 className="mx-auto mb-2 size-5 animate-spin text-muted-foreground" />
                            <div className="text-sm text-muted-foreground">
                              Loading server roles…
                            </div>
                          </div>
                        ) : selectedGuildRolesState.roles.length > 0 ? (
                          <div className="max-h-[320px] overflow-y-auto rounded-lg border">
                            <div className="divide-y">
                              {selectedGuildRolesState.roles.map(role => {
                                const selected = draft.selectedRoleIDs.includes(
                                  role.id
                                );
                                return (
                                  <button
                                    key={role.id}
                                    type="button"
                                    onClick={() => handleRoleToggle(role.id)}
                                    className={cn(
                                      'flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition-colors hover:bg-muted/20',
                                      selected && 'bg-muted/20'
                                    )}
                                  >
                                    <div className="min-w-0 space-y-0.5">
                                      <div className="truncate text-sm font-medium text-foreground">
                                        {role.name}
                                      </div>
                                      <div className="text-xs text-muted-foreground">
                                        {role.managed
                                          ? 'Managed'
                                          : role.hoist
                                            ? 'Hoisted'
                                            : 'Standard'}
                                      </div>
                                    </div>
                                    <div
                                      className={cn(
                                        'flex size-5 shrink-0 items-center justify-center rounded-sm border transition-colors',
                                        selected
                                          ? 'border-primary bg-primary text-primary-foreground'
                                          : 'border-input bg-background'
                                      )}
                                    >
                                      {selected ? (
                                        <Check className="size-3.5" />
                                      ) : null}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          <div className="rounded-lg border border-dashed px-4 py-8 text-center text-muted-foreground">
                            <Users className="mx-auto mb-2 size-7 opacity-60" />
                            <p className="text-sm">
                              No roles found yet. Refresh roles or use manual
                              role IDs below.
                            </p>
                          </div>
                        )}

                        {selectedGuild.ownership_state !== 'unclaimed' &&
                          (selectedGuildRolesState.error ||
                            selectedGuildRolesState.roles.length === 0) && (
                            <details className="group rounded-lg border p-3">
                              <summary className="cursor-pointer text-sm font-medium text-foreground">
                                Manual role entry
                              </summary>
                              <div className="space-y-2 pt-3">
                                <textarea
                                  value={draft.fallbackRoleIDsText}
                                  disabled={saving}
                                  onChange={event =>
                                    setDraft(current => ({
                                      ...current,
                                      fallbackRoleIDsText: event.target.value,
                                    }))
                                  }
                                  rows={3}
                                  className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring/30"
                                  placeholder="e.g. 1491312162952712262, 1491312162952712263"
                                />
                                <p className="text-xs text-muted-foreground">
                                  Use comma-separated Discord role IDs only if
                                  the live role list is unavailable.
                                </p>
                              </div>
                            </details>
                          )}
                      </div>

                      <div className="space-y-4 border-t pt-5">
                        <div className="rounded-lg border bg-muted/20 p-4">
                          <div className="text-xs font-medium text-muted-foreground">
                            Current selection
                          </div>
                          <div className="mt-1 text-sm font-medium text-foreground">
                            {selectedGuild.ownership_state === 'unclaimed'
                              ? 'Verification required before saving roles'
                              : formatRoleSummary(
                                  draft.selectedRoleIDs.length > 0
                                    ? draft.selectedRoleIDs
                                    : parseRoleIDs(draft.fallbackRoleIDsText),
                                  selectedGuildRoleLookup
                                )}
                          </div>
                        </div>

                        <div className="flex flex-col gap-2 sm:flex-row">
                          <Button
                            size="sm"
                            disabled={
                              saving ||
                              selectedGuild.ownership_state === 'unclaimed'
                            }
                            className="flex-1"
                            onClick={() => void handleSave()}
                          >
                            {saving ? (
                              <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                            ) : (
                              <Save className="mr-1.5 size-3.5" />
                            )}
                            {selectedGuild.ownership_state === 'unclaimed'
                              ? 'Verification required'
                              : 'Save server'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={saving}
                            onClick={() =>
                              handleSelectGuild(selectedGuild.guild_id)
                            }
                          >
                            Reset
                          </Button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center gap-3 py-10 text-center">
                      <div className="flex size-10 items-center justify-center rounded-lg border bg-muted/20">
                        <CircleDashed className="size-5 text-muted-foreground" />
                      </div>
                      <div className="space-y-1">
                        <h3 className="text-sm font-medium text-foreground">
                          No server selected
                        </h3>
                        <p className="max-w-[260px] text-sm text-muted-foreground">
                          Pick a server from the list to configure its roles and
                          Bridge settings.
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardContent className="space-y-4 p-6">
                  <div className="space-y-3 rounded-lg border border-violet-500/20 bg-violet-500/10 p-4 text-sm text-violet-900 dark:text-violet-100">
                    <div className="font-medium">
                      {selectedGuildNeedsVerification
                        ? 'Finish setup for the selected server'
                        : 'Connect a server in Discord'}
                    </div>
                    <div className="text-violet-800/90 dark:text-violet-100/80">
                      {selectedGuildNeedsVerification
                        ? 'This server still needs one Discord-side verification step before you can save role access.'
                        : 'Use one verification flow for a new server, a reinstalled server, or a server that needs to be reconnected.'}
                    </div>
                    <div className="space-y-3 rounded-lg border border-violet-500/20 bg-background/70 p-3">
                      {showGuildVerificationWorkflow ? (
                        <>
                          <div className="grid gap-3">
                            <div className="rounded-md border bg-muted/20 p-3">
                              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                Step 1
                              </div>
                              <div className="mt-1 text-sm font-medium text-foreground">
                                Add or re-authorize the public bot
                              </div>
                              <div className="mt-1 text-xs text-muted-foreground">
                                Open Discord, add the shared bot to the server
                                you want, then come back here for the
                                verification code.
                              </div>
                            </div>
                            <div className="rounded-md border bg-muted/20 p-3">
                              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                Step 2
                              </div>
                              <div className="mt-1 text-sm font-medium text-foreground">
                                Generate one verification code
                              </div>
                              <div className="mt-1 text-xs text-muted-foreground">
                                The same code path works whether the server is
                                brand new, already installed, or being
                                reconnected.
                              </div>
                            </div>
                            <div className="rounded-md border bg-muted/20 p-3">
                              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                Step 3
                              </div>
                              <div className="mt-1 text-sm font-medium text-foreground">
                                Run{' '}
                                <span className="font-mono">
                                  /bridgebot link
                                </span>{' '}
                                in the Discord server
                              </div>
                              <div className="mt-1 text-xs text-muted-foreground">
                                A Discord member with Manage Server or
                                Administrator permission needs to run the
                                command once in the target server.
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col gap-2 sm:flex-row">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleAddDiscordServer}
                              disabled={!installURL}
                            >
                              <Server className="mr-1.5 size-3.5" />
                              {getInstallButtonLabel(
                                pageData?.install_metadata ?? null
                              )}
                            </Button>
                            <Button
                              size="sm"
                              onClick={() =>
                                void handleCreateGuildVerificationCode()
                              }
                              disabled={guildVerificationLoading}
                            >
                              {guildVerificationLoading ? (
                                <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                              ) : (
                                <ShieldCheck className="mr-1.5 size-3.5" />
                              )}
                              {hasGuildVerificationCode
                                ? 'Generate new verification code'
                                : 'Generate verification code'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                void handleCopyGuildVerificationCode()
                              }
                              disabled={
                                copyingGuildVerificationCode ||
                                !hasGuildVerificationCode
                              }
                            >
                              {copyingGuildVerificationCode ? (
                                <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                              ) : (
                                <Copy className="mr-1.5 size-3.5" />
                              )}
                              Copy code
                            </Button>
                          </div>
                          {hasGuildVerificationCode ? (
                            <div className="space-y-2">
                              <div className="rounded-md border bg-muted/20 px-3 py-2 font-mono text-sm text-foreground">
                                {guildVerificationCode?.code}
                              </div>
                              <div className="text-xs text-violet-800/90 dark:text-violet-100/80">
                                Expires{' '}
                                {formatDateTime(
                                  guildVerificationCode!.expires_at
                                )}
                                . In Discord, run{' '}
                                <span className="font-mono">
                                  /bridgebot link
                                </span>{' '}
                                in the server you want to connect, paste the
                                code shown above, then refresh this page.
                              </div>
                            </div>
                          ) : (
                            <div className="text-xs text-violet-800/90 dark:text-violet-100/80">
                              After you add the bot in Discord, generate one
                              verification code here and use it once with{' '}
                              <span className="font-mono">/bridgebot link</span>{' '}
                              in the target server.
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div className="text-xs text-violet-800/90 dark:text-violet-100/80">
                            Need to connect another server? Start by adding or
                            re-authorizing the public bot in Discord. The
                            verification step will appear here as soon as you
                            start setup.
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleAddDiscordServer}
                            disabled={!installURL}
                          >
                            <Server className="mr-1.5 size-3.5" />
                            {getInstallButtonLabel(
                              pageData?.install_metadata ?? null
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>

                  {pageData?.runtime?.config_error ||
                  pageData?.runtime?.recent_error ? (
                    <div className="rounded-md border border-amber-500/20 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-400">
                      {pageData.runtime.config_error?.trim() ||
                        pageData.runtime.recent_error?.trim() ||
                        'Discord setup needs attention.'}
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            </div>
          </div>
        </>
      )}
    </SettingsLayout>
  );
}
